import * as React from 'react';
import { cn } from './lib/utils';

const GRID_CLASSES = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-2 xl:grid-cols-4',
  5: 'grid-cols-2 lg:grid-cols-5',
};

/**
 * Pulse-animation loading placeholder.
 *
 * Basic usage — matches any shape via className:
 *   <Skeleton className="h-4 w-32" />          // text line
 *   <Skeleton className="h-[96px] w-full" />   // KPI card
 *   <Skeleton className="h-8 w-8 rounded-full" /> // avatar
 *
 * Composed presets:
 *   <Skeleton.Text lines={3} />        // paragraph of 3 lines, last line shorter
 *   <Skeleton.KPICard />               // matches size="md" KPICard footprint
 *   <Skeleton.KPICard size="lg" />     // matches size="lg" footprint
 *   <Skeleton.TableRow columns={4} />  // table row with N cells
 */

function Skeleton({ className, ...props }) {
  return (
    <div
      className={cn('animate-pulse rounded bg-surface-warm', className)}
      aria-hidden="true"
      {...props}
    />
  );
}

// ── Text paragraph preset ─────────────────────────────────────────────────────
Skeleton.Text = function SkeletonText({ lines = 2, className }) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn('h-3', i === lines - 1 ? 'w-3/4' : 'w-full')}
        />
      ))}
    </div>
  );
};

// ── KPI card preset ───────────────────────────────────────────────────────────
const KPI_SIZE = {
  sm: { card: 'min-h-[72px]  p-3', label: 'h-2 w-16 mb-2',   value: 'h-4 w-12' },
  md: { card: 'min-h-[96px]  p-4', label: 'h-2.5 w-20 mb-2', value: 'h-8 w-16' },
  lg: { card: 'min-h-[128px] p-5', label: 'h-3 w-24 mb-2.5', value: 'h-11 w-20' },
  xl: { card: 'min-h-[160px] p-6', label: 'h-3 w-28 mb-3',   value: 'h-14 w-24' },
};

Skeleton.KPICard = function SkeletonKPICard({ size = 'md', className }) {
  const s = KPI_SIZE[size] ?? KPI_SIZE.md;
  return (
    <div
      className={cn(
        'rounded-card bg-nn-white border border-nn-border shadow-card overflow-hidden relative',
        s.card, className
      )}
      aria-hidden="true"
    >
      <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-card bg-surface-warm animate-pulse" />
      <div className="pl-4 flex flex-col gap-2">
        <Skeleton className={s.label} />
        <Skeleton className={s.value} />
        <Skeleton className="h-2.5 w-28" />
      </div>
    </div>
  );
};

// ── Table row preset ──────────────────────────────────────────────────────────
Skeleton.TableRow = function SkeletonTableRow({ columns = 4, className }) {
  return (
    <tr className={cn('border-b border-nn-border', className)} aria-hidden="true">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-3 w-full" />
        </td>
      ))}
    </tr>
  );
};

// ── KPI grid preset ─────────────────────────────────────────────────────────
Skeleton.KPIGrid = function SkeletonKPIGrid({ columns = 4, size = 'sm' }) {
  return (
    <div className={`grid gap-2 ${GRID_CLASSES[columns] ?? GRID_CLASSES[4]}`}>
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton.KPICard key={i} size={size} />
      ))}
    </div>
  );
};

export { Skeleton };
